import { forwardRef, useImperativeHandle, useRef } from 'react';
import { motion } from 'framer-motion';
import { cn } from '@/lib/utils';

type SessionEndTransitionProgressBarProps = {
	visible: boolean;
	top: number;
};

export type SessionEndTransitionProgressBarHandle = {
	setProgress: (value: number) => void;
};

const clampProgress = (value: number) => Math.min(1, Math.max(0, value));

const SessionEndTransitionProgressBar = forwardRef<
	SessionEndTransitionProgressBarHandle,
	SessionEndTransitionProgressBarProps
>(function SessionEndTransitionProgressBar({ visible, top }, ref) {
	const trackRef = useRef<HTMLDivElement>(null);
	const fillRef = useRef<HTMLDivElement>(null);
	const ariaValueRef = useRef(0);

	useImperativeHandle(
		ref,
		() => ({
			setProgress: (value: number) => {
				const clamped = clampProgress(value);

				if (fillRef.current) {
					fillRef.current.style.width = `${clamped * 100}%`;
				}

				const rounded = Math.round(clamped * 100);
				if (trackRef.current && rounded !== ariaValueRef.current) {
					ariaValueRef.current = rounded;
					trackRef.current.setAttribute(
						'aria-valuenow',
						String(rounded),
					);
				}
			},
		}),
		[],
	);

	return (
		<motion.div
			className="pointer-events-none absolute inset-x-0 z-10 flex justify-center px-6"
			style={{ top }}
			initial={false}
			animate={{ opacity: visible ? 1 : 0, y: visible ? 0 : 8 }}
			transition={{ duration: 0.35, ease: 'easeOut' }}
		>
			<div className="w-full max-w-[min(320px,calc(100vw-3rem))]">
				<div
					ref={trackRef}
					className={cn(
						'h-2.5 overflow-hidden rounded-full p-px',
						'border border-white/10 bg-[#141c26]/90',
						'shadow-[inset_0_1px_0_rgba(255,255,255,0.04)]',
					)}
					role="progressbar"
					aria-valuenow={0}
					aria-valuemin={0}
					aria-valuemax={100}
					aria-label="Progresso da geração do prontuário"
				>
					<div
						ref={fillRef}
						className={cn(
							'h-full min-w-0 rounded-full',
							'bg-gradient-to-r from-[#4f7fd6] via-[#8ab4f8] to-[#b6cffc]',
							'shadow-[0_0_14px_rgba(138,180,248,0.35)]',
						)}
						style={{ width: '0%' }}
					/>
				</div>
			</div>
		</motion.div>
	);
});

export { SessionEndTransitionProgressBar };
